"use client";

import {
  AddressAutocomplete,
  AddressComponents
} from "@/components/ui/address-autocomplete";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MultiSelect } from "@/components/ui/multi-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import React, { useEffect, useState } from "react";
import { Facility } from "../types";

interface FacilityModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (
    facilityData: Omit<
      Facility,
      "id" | "company_id" | "created_at" | "updated_at"
    >
  ) => void;
  facility?: Facility;
  title: string;
  isSaving?: boolean;
}

export function FacilityModal({
  open,
  onOpenChange,
  onSave,
  facility,
  title,
  isSaving = false
}: FacilityModalProps) {
  const [location, setLocation] = useState("");
  const [facilityType, setFacilityType] = useState("");
  const [selectedWorkingHours, setSelectedWorkingHours] = useState<string[]>(
    []
  );
  const [selectedShiftModels, setSelectedShiftModels] = useState<string[]>([]);
  const [addressComponents, setAddressComponents] = useState<AddressComponents>(
    {
      formattedAddress: ""
    }
  );
  console.log(addressComponents);
  useEffect(() => {
    if (facility) {
      setLocation(facility.location);
      setFacilityType(facility.facility_type);
      setSelectedWorkingHours(facility.working_hours);
      setSelectedShiftModels(facility.shift_models);
      setAddressComponents({
        formattedAddress: facility.address,
        street: facility.street,
        streetNumber: facility.street_number,
        postalCode: facility.postal_code,
        city: facility.city,
        state: facility.state,
        country: facility.country
      });
    } else {
      setLocation("");
      setFacilityType("");
      setSelectedWorkingHours([]);
      setSelectedShiftModels([]);
      setAddressComponents({
        formattedAddress: ""
      });
    }
  }, [facility, open]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const location = formData.get("location") as string;
    const facilityType = formData.get("facility_type") as string;
    const workingHours = formData.getAll("working_hours") as string[];
    const shiftModels = formData.getAll("shift_models") as string[];

    onSave({
      location,
      facility_type: facilityType,
      working_hours: workingHours,
      shift_models: shiftModels,
      address: addressComponents.formattedAddress,
      street: addressComponents.street,
      street_number: addressComponents.streetNumber,
      postal_code: addressComponents.postalCode,
      city: addressComponents.city,
      state: addressComponents.state,
      country: addressComponents.country,
      job_profiles: [],
      subsidiary_id: null
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {facility
              ? "Bearbeite die Einrichtungsinformationen"
              : "Füge eine neue Einrichtung hinzu"}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="location">Standort</Label>
            <Input
              id="location"
              name="location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Standort eingeben"
              required
              disabled={isSaving}
            />
          </div>
          <AddressAutocomplete
            label="Adresse"
            placeholder="Adresse eingeben"
            value={addressComponents.formattedAddress}
            onChange={setAddressComponents}
            required
            disabled={isSaving}
            name="address"
          />
          <div className="space-y-2">
            <Label htmlFor="facility_type">Einrichtungstyp</Label>
            <Select
              name="facility_type"
              value={facilityType}
              onValueChange={setFacilityType}
              disabled={isSaving}
            >
              <SelectTrigger>
                <SelectValue placeholder="Einrichtungstyp auswählen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hospital">Krankenhaus</SelectItem>
                <SelectItem value="nursing_home">Pflegeheim</SelectItem>
                <SelectItem value="clinic">Klinik</SelectItem>
                <SelectItem value="other">Sonstige</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Arbeitszeiten</Label>
            <MultiSelect
              name="working_hours"
              options={[
                { value: "full_time", label: "Vollzeit" },
                { value: "part_time", label: "Teilzeit" },
                { value: "shift", label: "Schicht" },
                { value: "flexible", label: "Flexibel" }
              ]}
              selectedValues={selectedWorkingHours}
              onValueChange={setSelectedWorkingHours}
              disabled={isSaving}
              modalPopover={true}
            />
          </div>
          <div className="space-y-2">
            <Label>Schichtmodelle</Label>
            <MultiSelect
              name="shift_models"
              options={[
                { value: "early", label: "Früh" },
                { value: "late", label: "Spät" },
                { value: "night", label: "Nacht" },
                { value: "weekend", label: "Wochenende" }
              ]}
              selectedValues={selectedShiftModels}
              onValueChange={setSelectedShiftModels}
              disabled={isSaving}
              modalPopover={true}
            />
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={isSaving}>
              {isSaving ? "Speichern..." : "Speichern"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
